import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TopbarControl from 'features/topbar/components/TopbarControl';
import { selectDeviceInfo } from 'features/application/applicationSlice';
import { useAppSelector } from 'store/hooks';

const isFullscreenApiAvailable = (): boolean =>
  !!(
    document.fullscreenEnabled ||
    // @ts-ignore - Safari/old WebKit
    document.webkitFullscreenEnabled ||
    // @ts-ignore - Firefox
    document.mozFullScreenEnabled ||
    // @ts-ignore - IE11
    document.msFullscreenEnabled
  );

const isFullscreenAllowedByPolicy = (): boolean => {
  try {
    // @ts-ignore - Permissions Policy API (Chrome 88+, Edge 88+)
    const policy = document.permissionsPolicy || document.featurePolicy;
    if (policy && typeof policy.allowsFeature === 'function') {
      return policy.allowsFeature('fullscreen');
    }
  } catch (error) {
    // API not available, assume allowed (will be caught by actual request)
  }

  // If we can't check the policy, assume it's allowed
  // The actual request will fail if it's not, and we'll catch that error
  return true;
};

const isFullscreenAvailable = (): boolean =>
  isFullscreenApiAvailable() && isFullscreenAllowedByPolicy();

const FullscreenModeControl = () => {
  const { t } = useTranslation('room');
  const [isFullscreen, setIsFullscreen] = useState(() => Boolean(document.fullscreenElement));
  const [hasPermissionError, setHasPermissionError] = useState(false);
  const deviceInfo = useAppSelector(selectDeviceInfo);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  if (!isFullscreenAvailable() || hasPermissionError) {
    return null;
  }

  const handleToggle = async () => {
    try {
      if (isFullscreen) {
        await document.exitFullscreen();
      } else {
        await document.documentElement.requestFullscreen();
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorName = error instanceof Error ? error.name : '';

      const isPermissionError =
        errorName === 'NotAllowedError' ||
        errorMessage.includes('permissions') ||
        errorMessage.includes('policy') ||
        errorMessage.includes('Fullscreen API');

      if (isPermissionError) {
        setHasPermissionError(true);
      }
    }
  };

  const tooltipText = isFullscreen
    ? t('topbar.fullscreeen.exit_tooltip')
    : t('topbar.fullscreeen.enter_tooltip');

  const buttonText = isFullscreen
    ? t('topbar.fullscreeen.btn_exit_label')
    : t('topbar.fullscreeen.btn_enter_label');

  const iconName = isFullscreen ? 'exit-fullscreen1' : 'enter-fullscreen';

  if (deviceInfo.os.name === 'iOS') {
    return null;
  }

  return (
    <TopbarControl
      tooltipText={tooltipText}
      onToggle={handleToggle}
      buttonText={buttonText}
      iconName={iconName}
      data-testid="fullscreen-control"
    />
  );
};

export default FullscreenModeControl;
